/**
 * Flags image-placeholder hosts (see snippets/image-placeholder.liquid) once
 * the real image inside them has loaded, so the blurred placeholder can fade
 * out. Listens in the capture phase on the document, so images swapped in
 * later, for example after a variant change re-renders a gallery or a
 * section is morphed, are covered without extra wiring.
 */
const HOST_SELECTOR = '.image-placeholder-host';
const PLACEHOLDER_CLASS = 'image-placeholder';

/**
 * The image a host is waiting for: the first non-placeholder <img> that
 * follows the placeholder in document order. Anything after it, such as a
 * hover image on a product card or an image block nested inside a slide,
 * must not fade the placeholder early.
 *
 * @param {HTMLElement} host
 * @returns {HTMLImageElement | null}
 */
const designatedImage = (host) => {
  const placeholder = host.querySelector(`:scope > .${PLACEHOLDER_CLASS}`);

  for (const candidate of host.querySelectorAll('img')) {
    if (candidate.classList.contains(PLACEHOLDER_CLASS)) continue;
    if (placeholder && !(placeholder.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
    if (candidate instanceof HTMLImageElement) return candidate;
  }

  return null;
};

/**
 * Flags the host once the image can actually be painted. `load` fires when
 * the bytes are in, but a large image may still be decoding, and fading the
 * placeholder before that leaves a blank tile. decode() resolves once the
 * pixels are ready; if it rejects (for example the src changed) fall back to
 * flagging straight away.
 *
 * @param {HTMLImageElement} image
 */
const markLoaded = async (image) => {
  if (image.classList.contains(PLACEHOLDER_CLASS)) return;

  const host = image.closest(HOST_SELECTOR);
  if (!(host instanceof HTMLElement) || designatedImage(host) !== image) return;

  try {
    await image.decode();
  } catch {
    // Fall through: better to show the image late than never fade the placeholder.
  }

  host.dataset.loaded = '';
};

document.addEventListener(
  'load',
  (event) => {
    if (event.target instanceof HTMLImageElement) markLoaded(event.target);
  },
  true
);

for (const image of document.querySelectorAll(`${HOST_SELECTOR} img`)) {
  if (image instanceof HTMLImageElement && image.complete && image.naturalWidth > 0) markLoaded(image);
}
